// src/utils/index.ts
var hexToRgba = (hex, opacity) => {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// src/themes/index.ts
var loginWithPhantomColor = "#7C63E7";
var darkTheme = {
  background: "#181818",
  text: "#FFFFFF",
  secondary: "#98979C",
  overlay: "rgba(0, 0, 0, 0.7)",
  borderRadius: "16px",
  error: "#F00000",
  success: "#1CC700",
  brand: loginWithPhantomColor
};
var lightTheme = {
  background: "#FFFFFF",
  text: "#181818",
  secondary: "#98979C",
  overlay: "rgba(0, 0, 0, 0.5)",
  borderRadius: "16px",
  error: "#F00000",
  success: "#1CC700",
  brand: loginWithPhantomColor
};
function mergeTheme(customTheme) {
  const secondary = customTheme?.secondary || darkTheme.secondary;
  const isHex = secondary.startsWith("#");
  if (!isHex) {
    throw new Error("Secondary color must be a hex color to derive auxiliary color.");
  }
  return {
    ...darkTheme,
    ...customTheme,
    aux: hexToRgba(secondary, 0.1),
    typography: {
      caption: {
        fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "17px",
        letterSpacing: "-0.14px"
      },
      captionBold: {
        fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: "600",
        lineHeight: "17px",
        letterSpacing: "-0.14px"
      },
      label: {
        fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: "12px",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "15px",
        letterSpacing: "-0.12px"
      }
    }
  };
}
function mergeThemeNative(customTheme) {
  const secondary = customTheme?.secondary || darkTheme.secondary;
  const isHex = secondary.startsWith("#");
  if (!isHex) {
    throw new Error("Secondary color must be a hex color to derive auxiliary color.");
  }
  return {
    ...darkTheme,
    ...customTheme,
    aux: hexToRgba(secondary, 0.1),
    typography: {
      caption: {
        fontFamily: "System",
        fontSize: 14,
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: 17,
        letterSpacing: -0.14
      },
      captionBold: {
        fontFamily: "System",
        fontSize: 14,
        fontStyle: "normal",
        fontWeight: "600",
        lineHeight: 17,
        letterSpacing: -0.14
      },
      label: {
        fontFamily: "System",
        fontSize: 12,
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: 15,
        letterSpacing: -0.12
      }
    }
  };
}

// src/themes/ThemeContext.tsx
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var ThemeContext = createContext(void 0);
function ThemeProvider({ children, theme }) {
  const resolvedTheme = mergeTheme(theme || darkTheme);
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value: { theme: resolvedTheme }, children });
}
function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context.theme;
}

// src/components/Button.web.tsx
import { useState, useMemo } from "react";

// src/components/Icon.web.tsx
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var ICONS = {
  phantom: {
    viewBox: "0 0 20 20",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        d: "M2.36655 18.3271C4.91871 18.3271 6.8367 16.1076 7.98131 14.3537C7.8421 14.7417 7.76476 15.1298 7.76476 15.5023C7.76476 16.5267 8.35253 17.2562 9.5126 17.2562C11.1058 17.2562 12.8072 15.8593 13.6889 14.3537C13.627 14.571 13.5961 14.7728 13.5961 14.959C13.5961 15.673 13.9982 16.1231 14.818 16.1231C17.4011 16.1231 19.9997 11.5444 19.9997 7.53987C19.9997 4.42011 18.422 1.67285 14.4622 1.67285C7.50181 1.67285 0 10.1785 0 15.673C0 17.8305 1.16007 18.3271 2.36655 18.3271ZM12.0648 7.19841C12.0648 6.42235 12.4979 5.8791 13.132 5.8791C13.7507 5.8791 14.1838 6.42235 14.1838 7.19841C14.1838 7.97447 13.7507 8.53323 13.132 8.53323C12.4979 8.53323 12.0648 7.97447 12.0648 7.19841ZM15.3748 7.19841C15.3748 6.42235 15.8079 5.8791 16.4421 5.8791C17.0608 5.8791 17.4939 6.42235 17.4939 7.19841C17.4939 7.97447 17.0608 8.53323 16.4421 8.53323C15.8079 8.53323 15.3748 7.97447 15.3748 7.19841Z",
        fill: "currentColor"
      }
    )
  },
  google: {
    viewBox: "0 0 20 20",
    path: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(
        "mask",
        {
          id: "mask0_google",
          style: { maskType: "luminance" },
          maskUnits: "userSpaceOnUse",
          x: "0",
          y: "0",
          width: "20",
          height: "20",
          children: [
            /* @__PURE__ */ jsx2(
              "path",
              {
                d: "M18.8 10.2084C18.8 9.55837 18.7417 8.93337 18.6333 8.33337H10V11.8834H14.9333C14.7167 13.025 14.0667 13.9917 13.0917 14.6417V16.95H16.0667C17.8 15.35 18.8 13 18.8 10.2084Z",
                fill: "#FFFFFE"
              }
            ),
            /* @__PURE__ */ jsx2(
              "path",
              {
                d: "M9.99998 19.1667C12.475 19.1667 14.55 18.35 16.0667 16.95L13.0917 14.6417C12.275 15.1917 11.2333 15.525 9.99998 15.525C7.61665 15.525 5.59165 13.9167 4.86665 11.75H1.81665V14.1167C3.32498 17.1083 6.41665 19.1667 9.99998 19.1667Z",
                fill: "#FFFFFE"
              }
            ),
            /* @__PURE__ */ jsx2(
              "path",
              {
                d: "M4.86659 11.7417C4.68325 11.1917 4.57492 10.6084 4.57492 10.0001C4.57492 9.39172 4.68325 8.80839 4.86659 8.25839V5.89172H1.81659C1.19159 7.12506 0.833252 8.51672 0.833252 10.0001C0.833252 11.4834 1.19159 12.8751 1.81659 14.1084L4.19159 12.2584L4.86659 11.7417Z",
                fill: "#FFFFFE"
              }
            ),
            /* @__PURE__ */ jsx2(
              "path",
              {
                d: "M9.99998 4.48337C11.35 4.48337 12.55 4.95004 13.5083 5.85004L16.1333 3.22504C14.5417 1.74171 12.475 0.833374 9.99998 0.833374C6.41665 0.833374 3.32498 2.89171 1.81665 5.89171L4.86665 8.25837C5.59165 6.09171 7.61665 4.48337 9.99998 4.48337Z",
                fill: "#FFFFFE"
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx2("g", { mask: "url(#mask0_google)", children: /* @__PURE__ */ jsx2("rect", { width: "20", height: "20", fill: "currentColor" }) })
    ] })
  },
  apple: {
    viewBox: "0 0 20 20",
    path: /* @__PURE__ */ jsxs("g", { clipPath: "url(#clip0_apple)", children: [
      /* @__PURE__ */ jsx2(
        "path",
        {
          d: "M16.9876 6.91634C16.8812 6.99884 15.0039 8.05667 15.0039 10.4088C15.0039 13.1295 17.3927 14.092 17.4642 14.1158C17.4532 14.1745 17.0847 15.434 16.2047 16.7173C15.4201 17.8467 14.6006 18.9742 13.3539 18.9742C12.1072 18.9742 11.7864 18.25 10.3472 18.25C8.94473 18.25 8.44606 18.998 7.30573 18.998C6.1654 18.998 5.36973 17.953 4.4549 16.6697C3.39523 15.1627 2.53906 12.8215 2.53906 10.5995C2.53906 7.0355 4.8564 5.14534 7.13706 5.14534C8.3489 5.14534 9.35906 5.941 10.1199 5.941C10.8441 5.941 11.9734 5.09767 13.3521 5.09767C13.8746 5.09767 15.7519 5.14534 16.9876 6.91634ZM12.6976 3.58884C13.2677 2.91234 13.6711 1.97367 13.6711 1.035C13.6711 0.904837 13.6601 0.772837 13.6362 0.666504C12.7086 0.701337 11.6049 1.28434 10.9394 2.05617C10.4169 2.65017 9.92923 3.58884 9.92923 4.54034C9.92923 4.68334 9.95306 4.82634 9.96406 4.87217C10.0227 4.88317 10.1181 4.896 10.2134 4.896C11.0457 4.896 12.0926 4.33867 12.6976 3.58884Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx2("defs", { children: /* @__PURE__ */ jsx2("clipPath", { id: "clip0_apple", children: /* @__PURE__ */ jsx2("rect", { width: "14.9233", height: "18.3333", fill: "white", transform: "translate(2.53906 0.833374)" }) }) })
    ] })
  },
  wallet: {
    viewBox: "0 0 19 15",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M0 3.33333C0 1.49238 1.49238 0 3.33333 0H15C16.8409 0 18.3333 1.49238 18.3333 3.33333V11.6667C18.3333 13.5076 16.8409 15 15 15H3.33333C1.49238 15 0 13.5076 0 11.6667V3.33333ZM1.66667 8.33333V11.6667C1.66667 12.5871 2.41286 13.3333 3.33333 13.3333H15C15.9205 13.3333 16.6667 12.5871 16.6667 11.6667V8.33333H13.3333C12.9727 8.33333 12.6218 8.4503 12.3333 8.66667L11.1667 9.54167C9.98148 10.4306 8.35185 10.4306 7.16667 9.54167L6 8.66667C5.71151 8.4503 5.36062 8.33333 5 8.33333H1.66667ZM16.6667 6.66667C16.6667 5.74619 15.9205 5 15 5H3.33333C2.41286 5 1.66667 5.74619 1.66667 6.66667H5C5.72123 6.66667 6.42301 6.90059 7 7.33333L8.16667 8.20833C8.75926 8.65278 9.57407 8.65278 10.1667 8.20833L11.3333 7.33333C11.9103 6.90059 12.6121 6.66667 13.3333 6.66667H16.6667ZM16.6667 3.77927V3.33333C16.6667 2.41286 15.9205 1.66667 15 1.66667H3.33333C2.41286 1.66667 1.66667 2.41286 1.66667 3.33333V3.77927C2.15696 3.49566 2.72619 3.33333 3.33333 3.33333H15C15.6071 3.33333 16.1764 3.49566 16.6667 3.77927Z",
        fill: "currentColor"
      }
    )
  },
  "chevron-right": {
    viewBox: "0 0 16 16",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M5.52876 2.86189C5.78911 2.60154 6.21122 2.60154 6.47157 2.86189L11.1382 7.52855C11.3986 7.7889 11.3986 8.21101 11.1382 8.47136L6.47157 13.138C6.21122 13.3984 5.78911 13.3984 5.52876 13.138C5.26841 12.8777 5.26841 12.4556 5.52876 12.1952L9.72402 7.99996L5.52876 3.8047C5.26841 3.54435 5.26841 3.12224 5.52876 2.86189Z",
        fill: "currentColor"
      }
    )
  },
  "arrow-right": {
    viewBox: "0 0 16 16",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        d: "M8 3L13 8L8 13",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        fill: "none"
      }
    )
  },
  x: {
    viewBox: "0 0 16 16",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M2.86201 2.86201C3.12236 2.60166 3.54447 2.60166 3.80482 2.86201L8.00008 7.05727L12.1953 2.86201C12.4557 2.60166 12.8778 2.60166 13.1382 2.86201C13.3985 3.12236 13.3985 3.54447 13.1382 3.80482L8.94289 8.00008L13.1382 12.1953C13.3985 12.4557 13.3985 12.8778 13.1382 13.1382C12.8778 13.3985 12.4557 13.3985 12.1953 13.1382L8.00008 8.94289L3.80482 13.1382C3.54447 13.3985 3.12236 13.3985 2.86201 13.1382C2.60166 12.8778 2.60166 12.4557 2.86201 12.1953L7.05727 8.00008L2.86201 3.80482C2.60166 3.54447 2.60166 3.12236 2.86201 2.86201Z",
        fill: "currentColor"
      }
    )
  },
  tiktok: {
    viewBox: "0 0 20 20",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        d: "M17.0156 7.54948C15.4792 7.54167 14.0156 6.94271 12.8958 5.89323V13.0573C12.8958 16.1042 10.401 18.599 7.35417 18.599C6.24219 18.599 5.21615 18.276 4.35417 17.7292C2.91927 16.8333 1.8776 15.3281 1.60938 13.5521C1.57813 13.3307 1.5625 13.1042 1.5625 12.8724C1.5625 9.82552 4.05729 7.33073 7.10417 7.33073C7.35156 7.33073 7.59375 7.35156 7.83073 7.38802V10.2708C7.59635 10.2161 7.35417 10.1849 7.10417 10.1849C5.63542 10.1849 4.44271 11.3776 4.44271 12.8464C4.44271 13.8333 4.97396 14.6875 5.76563 15.138C6.15104 15.3568 6.59375 15.4818 7.0651 15.4818C8.47917 15.4818 9.64323 14.3776 9.73438 13.0026C9.74219 12.9271 9.74479 5.3724 9.74479 5.3724H12.5469C12.6354 6.21615 12.9401 6.99479 13.4036 7.65885C14.0469 8.57552 14.9818 9.25521 16.0677 9.54948V7.54948H17.0156Z",
        fill: "currentColor"
      }
    )
  },
  close: {
    viewBox: "0 0 24 24",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        d: "M18 6L6 18M6 6L18 18",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        fill: "none"
      }
    )
  },
  solana: {
    viewBox: "0 0 9 9",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M1.54607 6.17667C1.57509 6.14475 1.61047 6.11923 1.64992 6.10177C1.68937 6.08429 1.73204 6.07526 1.77519 6.07523L8.84482 6.08101C8.87493 6.08107 8.90437 6.08988 8.92958 6.10636C8.95478 6.12283 8.97465 6.14627 8.98678 6.17382C8.99891 6.20137 9.00278 6.23185 8.99792 6.26156C8.99306 6.29127 8.97968 6.31894 8.95939 6.34119L7.45389 7.99886C7.42484 8.0308 7.38944 8.05634 7.34995 8.0738C7.31046 8.09128 7.26775 8.1003 7.22457 8.1003L0.155133 8.09452C0.125026 8.09446 0.0955847 8.08565 0.0703861 8.06917C0.0451864 8.0527 0.0253137 8.02927 0.0131815 8.00171C0.00104843 7.97416 -0.00282139 7.94368 0.00204072 7.91397C0.00690283 7.88426 0.0202886 7.85659 0.0405712 7.83434L1.54607 6.17667ZM8.95939 4.79673C8.97968 4.81898 8.99306 4.84664 8.99792 4.87635C9.00278 4.90606 8.99891 4.93655 8.98678 4.9641C8.97465 4.99166 8.95478 5.01509 8.92958 5.03156C8.90437 5.04804 8.87493 5.05684 8.84482 5.0569L1.77538 5.06269C1.73221 5.06269 1.6895 5.05366 1.65001 5.03619C1.61052 5.01873 1.57512 4.99319 1.54607 4.96124L0.0405712 3.30261C0.0202886 3.28036 0.00690283 3.2527 0.00204072 3.22298C-0.00282139 3.19327 0.00104843 3.16279 0.0131815 3.13524C0.0253137 3.10768 0.0451864 3.08425 0.0703861 3.06778C0.0955847 3.0513 0.125026 3.04249 0.155133 3.04243L7.22476 3.03665C7.26792 3.03668 7.31059 3.04571 7.35004 3.06319C7.38949 3.08065 7.42487 3.10617 7.45389 3.1381L8.95939 4.79673ZM1.54607 0.101447C1.57509 0.0695195 1.61047 0.0440047 1.64992 0.0265358C1.68937 0.00906688 1.73204 2.87448e-05 1.77519 0L8.84482 0.00578593C8.87493 0.00584714 8.90437 0.0146497 8.92958 0.0311243C8.95478 0.0475988 8.97465 0.0710365 8.98678 0.0985903C8.99891 0.126144 9.00278 0.156628 8.99792 0.18634C8.99306 0.21605 8.97968 0.243711 8.95939 0.26596L7.45389 1.92363C7.42484 1.95558 7.38944 1.98111 7.34995 1.99858C7.31046 2.01605 7.26775 2.02508 7.22457 2.02508L0.155133 2.01929C0.125026 2.01923 0.0955847 2.01043 0.0703861 1.99395C0.0451864 1.97748 0.0253137 1.95404 0.0131815 1.92648C0.00104843 1.89893 -0.00282139 1.86845 0.00204072 1.83874C0.00690283 1.80903 0.0202886 1.78136 0.0405712 1.75912L1.54607 0.101447Z",
        fill: "currentColor"
      }
    )
  },
  ethereum: {
    viewBox: "0 0 7 11",
    path: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx2("path", { d: "M6.44558 5.3474L3.22279 7.25238L0 5.34739L3.22279 0L6.44558 5.3474Z", fill: "currentColor" }),
      /* @__PURE__ */ jsx2("path", { d: "M6.44755 5.95857L3.22282 10.5L0 5.95852L3.22282 7.8626L6.44755 5.95857Z", fill: "currentColor" })
    ] })
  },
  bitcoin: {
    viewBox: "0 0 8 11",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        d: "M6.11234 4.86004C6.66737 4.57405 7.01465 4.07038 6.93353 3.23129C6.82428 2.08459 5.84538 1.70007 4.60964 1.59057L4.6094 0H3.65195L3.65168 1.54874C3.39976 1.54874 3.14296 1.55375 2.88733 1.55893L2.88712 0.000211106L1.93052 0.000120638L1.93035 1.5903C1.72303 1.59458 1.51944 1.59865 1.32064 1.59865V1.59395L0 1.5934L0.000178762 2.62728C0.000178762 2.62728 0.707241 2.61383 0.695593 2.62689C1.08327 2.62716 1.20985 2.85486 1.24625 3.0514L1.24655 4.86339V7.40864C1.22948 7.53199 1.15765 7.72907 0.886584 7.72943C0.898918 7.74041 0.190097 7.72931 0.190097 7.72931L0.000178762 8.88543H1.24595C1.47809 8.8857 1.70617 8.88935 1.92993 8.89116L1.93076 10.4997L2.88712 10.5L2.88685 8.90829C3.14997 8.91371 3.40393 8.91607 3.65201 8.91573L3.65174 10.5H4.60916L4.6097 8.89411C6.21908 8.80062 7.34578 8.39056 7.48586 6.86191C7.59876 5.63118 7.02606 5.08182 6.11234 4.86004ZM2.91021 2.69707C3.4502 2.69707 5.14847 2.52315 5.14871 3.66406C5.14847 4.75787 3.45074 4.63009 2.91021 4.63009V2.69707ZM2.90979 7.73728L2.91021 5.60594C3.559 5.60576 5.59394 5.41712 5.59421 6.67113C5.59448 7.87371 3.559 7.73679 2.90979 7.73728Z",
        fill: "currentColor"
      }
    )
  },
  sui: {
    viewBox: "0 0 8 9",
    path: /* @__PURE__ */ jsx2(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M5.63424 3.75325C6.00135 4.21419 6.22089 4.79712 6.22089 5.43123C6.22089 6.06533 5.99468 6.66605 5.61793 7.12957L5.58528 7.16963L5.57676 7.11882C5.56935 7.0758 5.56083 7.03205 5.55081 6.9883C5.36206 6.15914 4.74725 5.44829 3.73527 4.87239C3.05184 4.48452 2.66063 4.01802 2.55793 3.48738C2.49155 3.14438 2.54086 2.79988 2.63615 2.5047C2.73147 2.2099 2.87311 1.96257 2.99363 1.81387L3.38744 1.33254C3.45642 1.248 3.58584 1.248 3.65479 1.33254L5.63462 3.75325H5.63424ZM6.25684 3.2723L3.61809 0.0461671C3.56767 -0.015389 3.47346 -0.015389 3.42305 0.0461671L0.784654 3.2723L0.776128 3.28307C0.290724 3.88565 0 4.6514 0 5.485C0 7.42624 1.57636 9 3.52057 9C5.46479 9 7.04113 7.42624 7.04113 5.485C7.04113 4.6514 6.7504 3.88565 6.26501 3.28342L6.25647 3.27267L6.25684 3.2723ZM1.41616 3.74288L1.652 3.454L1.65905 3.5074C1.66461 3.54967 1.67166 3.59194 1.67981 3.63459C1.83259 4.43594 2.37807 5.10379 3.28992 5.62108C4.08273 6.07237 4.54439 6.59116 4.67714 7.15999C4.73277 7.39732 4.74241 7.63092 4.71831 7.83526L4.71683 7.84786L4.70533 7.85342C4.3475 8.02807 3.94515 8.12634 3.52019 8.12634C2.02913 8.12634 0.820254 6.9197 0.820254 5.43085C0.820254 4.79156 1.04312 4.20454 1.41542 3.74213L1.41616 3.74288Z",
        fill: "currentColor"
      }
    )
  }
};
function Icon({ type, size = 20, color, style }) {
  const icon = ICONS[type];
  const iconStyle = {
    width: `${size}px`,
    height: `${size}px`,
    display: "inline-block",
    color: color || "currentColor",
    ...style
  };
  return /* @__PURE__ */ jsx2("svg", { style: iconStyle, viewBox: icon.viewBox, fill: "none", xmlns: "http://www.w3.org/2000/svg", children: icon.path });
}

// src/components/Text.web.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function Text({ children, variant = "caption", color, style }) {
  const theme = useTheme();
  const textStyle = {
    ...theme.typography[variant],
    color: color ?? theme.text,
    margin: 0,
    ...style
  };
  return /* @__PURE__ */ jsx3("span", { style: textStyle, children });
}

// src/components/Button.web.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var useBaseButtonStyle = ({ fullWidth, disabled, fontSize, fontWeight }) => {
  const theme = useTheme();
  return {
    height: "56px",
    padding: "12px 16px",
    width: fullWidth ? "100%" : "auto",
    border: "none",
    borderRadius: theme.borderRadius,
    fontFamily: theme.typography.captionBold.fontFamily,
    fontSize: fontSize ?? theme.typography.captionBold.fontSize,
    fontStyle: theme.typography.captionBold.fontStyle,
    fontWeight: fontWeight ?? theme.typography.captionBold.fontWeight,
    lineHeight: theme.typography.captionBold.lineHeight,
    letterSpacing: theme.typography.captionBold.letterSpacing,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background-color 0.2s",
    gap: "8px"
  };
};
function Button({
  children,
  onClick,
  disabled = false,
  variant = "primary",
  fullWidth = true,
  isLoading = false
}) {
  const theme = useTheme();
  const [isHovering, setIsHovering] = useState(false);
  const isInteractive = !disabled && !isLoading;
  const baseStyle = useBaseButtonStyle({
    fullWidth,
    disabled: disabled || isLoading
  });
  const backgroundColor = useMemo(() => {
    if (!isInteractive) {
      return variant === "primary" ? theme.aux : "transparent";
    }
    if (isHovering) {
      return variant === "primary" ? hexToRgba(theme.secondary, 0.15) : hexToRgba(theme.secondary, 0.1);
    }
    return variant === "primary" ? theme.aux : "transparent";
  }, [isInteractive, isHovering, variant, theme.aux, theme.secondary]);
  const buttonStyle = {
    ...baseStyle,
    backgroundColor,
    color: theme.text,
    border: variant === "secondary" ? `1px solid ${theme.secondary}` : "none"
  };
  const handleMouseEnter = () => {
    if (isInteractive) {
      setIsHovering(true);
    }
  };
  const handleMouseLeave = () => {
    setIsHovering(false);
  };
  return /* @__PURE__ */ jsx4(
    "button",
    {
      style: buttonStyle,
      onClick,
      disabled: disabled || isLoading,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: isLoading ? "Connecting..." : children
    }
  );
}
function LoginWithPhantomButton({
  children = "Continue with Phantom",
  onClick,
  disabled = false,
  fullWidth = true,
  isLoading = false,
  testId = "login-with-phantom-button"
}) {
  const theme = useTheme();
  const [isHovering, setIsHovering] = useState(false);
  const isInteractive = !disabled && !isLoading;
  const baseStyle = useBaseButtonStyle({
    fullWidth,
    disabled: disabled || isLoading
  });
  const backgroundColor = useMemo(() => {
    if (!isInteractive) {
      return theme.brand;
    }
    if (isHovering) {
      return hexToRgba(theme.brand, 0.85);
    }
    return theme.brand;
  }, [isInteractive, isHovering, theme.brand]);
  const buttonStyle = {
    ...baseStyle,
    backgroundColor,
    color: "#FFFFFF"
  };
  const handleMouseEnter = () => {
    if (isInteractive) {
      setIsHovering(true);
    }
  };
  const handleMouseLeave = () => {
    setIsHovering(false);
  };
  const buttonContentStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    width: "100%"
  };
  const buttonLeftStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  };
  const buttonRightStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  };
  return /* @__PURE__ */ jsx4(
    "button",
    {
      style: buttonStyle,
      onClick,
      disabled: disabled || isLoading,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      "data-testid": testId,
      children: /* @__PURE__ */ jsxs2("span", { style: buttonContentStyle, children: [
        /* @__PURE__ */ jsxs2("span", { style: buttonLeftStyle, children: [
          /* @__PURE__ */ jsx4(Icon, { type: "phantom", size: 20 }),
          /* @__PURE__ */ jsx4(Text, { variant: "captionBold", color: "#FFFFFF", children: isLoading ? "Connecting..." : children })
        ] }),
        /* @__PURE__ */ jsx4("span", { style: buttonRightStyle, children: /* @__PURE__ */ jsx4(Icon, { type: "chevron-right", size: 16, color: "#FFFFFF" }) })
      ] })
    }
  );
}

// src/components/BoundedIcon.web.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function BoundedIcon({ type, size = 20, color, style, background }) {
  const theme = useTheme();
  const iconBackgroundStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    backgroundColor: background ?? theme.aux
  };
  return /* @__PURE__ */ jsx5("div", { style: { ...iconBackgroundStyle, ...style }, children: /* @__PURE__ */ jsx5(Icon, { type, size, color }) });
}

// src/components/Skeleton.web.tsx
import { Fragment as Fragment2, jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
function Skeleton({ width = "100%", height = "20px", borderRadius, style }) {
  const theme = useTheme();
  const skeletonStyle = {
    width,
    height,
    borderRadius: borderRadius ?? theme.borderRadius,
    backgroundColor: theme.aux,
    animation: "pulse 1.5s ease-in-out infinite",
    ...style
  };
  return /* @__PURE__ */ jsxs3(Fragment2, { children: [
    /* @__PURE__ */ jsx6("style", { children: `
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        ` }),
    /* @__PURE__ */ jsx6("div", { style: skeletonStyle })
  ] });
}

// src/components/Modal.web.tsx
import { useRef, useEffect, useState as useState2 } from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
function Modal({
  appIcon: _appIcon,
  appName: _appName,
  isVisible,
  onClose,
  isMobile = false,
  children
}) {
  const theme = useTheme();
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState2("auto");
  useEffect(() => {
    if (!isVisible || !contentRef.current)
      return;
    const measureHeight = () => {
      if (contentRef.current) {
        const height = contentRef.current.scrollHeight;
        setContentHeight(height);
      }
    };
    measureHeight();
    const resizeObserver = new ResizeObserver(() => {
      measureHeight();
    });
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [isVisible, children]);
  if (!isVisible)
    return null;
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: isMobile ? "16px" : "0"
  };
  const modalStyle = {
    backgroundColor: theme.background,
    borderRadius: theme.borderRadius,
    maxWidth: isMobile ? "100%" : "350px",
    width: "100%",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    position: "relative",
    overflow: "hidden",
    transition: "max-width 0.1s ease-in-out"
  };
  const modalContentWrapperStyle = {
    height: typeof contentHeight === "number" ? `${contentHeight}px` : "auto",
    transition: "height 0.15s ease-in-out",
    overflow: "hidden"
  };
  const modalContentStyle = {
    transition: "opacity 0.15s ease-in-out"
  };
  return /* @__PURE__ */ jsx7("div", { style: overlayStyle, onClick: onClose, children: /* @__PURE__ */ jsx7("div", { style: modalStyle, onClick: (e) => e.stopPropagation(), children: /* @__PURE__ */ jsx7("div", { style: modalContentWrapperStyle, children: /* @__PURE__ */ jsx7("div", { ref: contentRef, style: modalContentStyle, children }) }) }) });
}

// src/components/ModalHeader.web.tsx
import { jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
function ModalHeader({ goBack = false, onGoBack, title, onClose, hideCloseButton = false }) {
  const theme = useTheme();
  const headerStyle = {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "28px 32px 0 32px",
    height: "32px"
  };
  const titleStyle = {
    fontFeatureSettings: '"liga" off, "clig" off'
  };
  const backButtonStyle = {
    position: "absolute",
    left: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: "8px",
    background: "none",
    border: "none",
    width: "32px",
    height: "32px"
  };
  const closeButtonStyle = {
    position: "absolute",
    right: "32px",
    background: "none",
    border: "none",
    color: theme.secondary,
    fontSize: "24px",
    cursor: "pointer",
    padding: "4px 8px",
    lineHeight: 1,
    transition: "color 0.2s",
    width: "auto"
  };
  return /* @__PURE__ */ jsxs4("div", { style: headerStyle, children: [
    goBack && onGoBack && /* @__PURE__ */ jsx8("button", { style: backButtonStyle, onClick: onGoBack, children: /* @__PURE__ */ jsx8(Icon, { type: "chevron-right", size: 16, color: theme.secondary, style: { transform: "rotate(180deg)" } }) }),
    /* @__PURE__ */ jsx8("div", { style: titleStyle, children: /* @__PURE__ */ jsx8(Text, { variant: "caption", color: theme.secondary, children: title }) }),
    onClose && !hideCloseButton && /* @__PURE__ */ jsx8("button", { style: closeButtonStyle, onClick: onClose, children: /* @__PURE__ */ jsx8(Icon, { type: "x", size: 16, color: theme.secondary }) })
  ] });
}
export {
  BoundedIcon,
  Button,
  Icon,
  LoginWithPhantomButton,
  Modal,
  ModalHeader,
  Skeleton,
  Text,
  ThemeProvider,
  darkTheme,
  hexToRgba,
  lightTheme,
  mergeTheme,
  mergeThemeNative,
  useTheme
};
